import { IGpsTrackingSetting } from "@kuika/kuika-cl-model";
import { ELocationAccuray, ICommonSettings } from "@kuika/kuika-cl-model/src/models/common-config";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { ReactNode } from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSwitch from "../../../../ui-kit/kuika-switch/kuika-switch";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigGpsTrackingSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingGpsTracking?: IGpsTrackingSetting;
  popoverVisible: boolean;
}

export interface IConfigGpsTrackingSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingGpsTrackingId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigGpsTrackingSettings extends React.PureComponent<
  IConfigGpsTrackingSettingsProps,
  IConfigGpsTrackingSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigGpsTrackingSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      editingGpsTracking: undefined,
      popoverVisible: false
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigGpsTrackingSettingsProps) {
    if (prevProps.editingGpsTrackingId !== this.props.editingGpsTrackingId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (this.props.editingGpsTrackingId) {
      const setting = this.props.commonSettings.gpsTrackingSettings?.find(
        (x) => x.id === this.props.editingGpsTrackingId
      );

      this.setState({ editingGpsTracking: setting }, () => {
        if (setting) {
          const fieldsValue = this.formRef.current?.getFieldsValue();
          this.formRef.current?.resetFields();

          fieldsValue.name = setting.name;
          fieldsValue.distanceBetweenLocationUpdates = setting.distanceBetweenLocationUpdates;
          fieldsValue.locationAccuracy = setting.locationAccuracy;
          fieldsValue.backgroundLocationTrackingEnabled = setting.backgroundLocationTrackingEnabled;

          this.formRef.current?.setFieldsValue(fieldsValue);
        }
      });
    }
  };

  existingNameValidator = async (_rule, value) => {
    const settings = this.props.commonSettings?.gpsTrackingSettings;

    const isSettingAlreadyExist =
      settings?.some((x) => x.name === value && x.id !== this.props.editingGpsTrackingId) || value === "Default";

    if (isSettingAlreadyExist) throw new Error("GPS Tracking Setting Name is already exist.");
  };

  integerValidator = async (_rule, value) => {
    if (!`${value}`.trim()) throw new Error("Please enter a distance value");

    if (!/^\+?(0|[1-9]\d*)$/.test(value)) throw new Error("Please enter a valid integer value");

    return;
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: IGpsTrackingSetting;
    if (this.props.editingGpsTrackingId) {
      obj = {
        id: this.props.editingGpsTrackingId,
        name: data.name,
        locationAccuracy: data.locationAccuracy,
        distanceBetweenLocationUpdates: parseInt(data.distanceBetweenLocationUpdates),
        backgroundLocationTrackingEnabled: data.backgroundLocationTrackingEnabled
      };

      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "gpsTrackingSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      obj = {
        id: Guid.create().toString(),
        name: data.name,
        locationAccuracy: data.locationAccuracy,
        distanceBetweenLocationUpdates: parseInt(data.distanceBetweenLocationUpdates),
        backgroundLocationTrackingEnabled: data.backgroundLocationTrackingEnabled
      };
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "gpsTrackingSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteGpsTracking = () => {
    if (!this.props.editingGpsTrackingId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    const parentDataKey = this.props.formInputName[0] === "androidConfig" ? "iosConfig" : "androidConfig";

    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingGpsTrackingId || "",
      configKey: "gpsTrackingSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Gps tracking setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      shouldControlOtherSections: {
        listOfControl: [[parentDataKey, this.props.formInputName[1]]],
        onCannotDelete: () => {
          KMainFunctions.displayErrorNotification(`
          This GPS Tracking setting is
          used on ${this.props.formInputName[0] === "androidConfig" ? "IOS" : "Android"}
          section. Please discard this setting
          on other config section before delete.
        `);
        }
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Gps tracking"
        secondHeader="Gps tracking"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingGpsTrackingId ? this.deleteGpsTracking : undefined}
            deleteTestId="gpsTrackingSettingDeleteButton"
            submitTestId={`gpsTrackingSetting${this.props.editingGpsTrackingId ? "Update" : "Create"}Button`}
            submitLabel={this.props.editingGpsTrackingId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configGpsTrackerForm"
          />
        }
      >
        <Form
          id="configGpsTrackerForm"
          name="gpsTrackingSettings"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody description={this.props.getSectionDescription("gpstracking_gpstracking_description")}>
            <ConfigField label="Name" required tooltipContentKey="gpstracking_gpstracking_name">
              <Form.Item
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Please enter a GPS Tracking Setting Name."
                  },
                  {
                    pattern: /^[A-Za-z][A-Za-z0-9 ]+$/,
                    message: "GPS Tracking Setting Name is invalid"
                  },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="gpsTrackingSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>

            <ConfigField
              label="Min. distance between updates"
              required
              tooltipContentKey="gpstracking_gpstracking_minimumdistancebetweenupdates"
            >
              <Form.Item name="distanceBetweenLocationUpdates" rules={[{ validator: this.integerValidator }]}>
                <KuikaInput
                  size="md"
                  data-testid="gpsTrackingSettingMinDistanceInput"
                  type="number"
                  rightUnit="meters"
                />
              </Form.Item>
            </ConfigField>

            <ConfigField
              label="Location Accuracy"
              required
              tooltipContentKey="gpstracking_gpstracking_locationaccuracy"
            >
              <Form.Item
                name="locationAccuracy"
                rules={[
                  {
                    required: true,
                    message: "Please select a Location Accuracy"
                  }
                ]}
              >
                <KuikaSelectbox
                  size="md"
                  data-testid="gpsTrackingSettingLocationAccuracyDropdown"
                  options={[
                    {
                      value: ELocationAccuray.Approximate,
                      label: "Approximate",
                      testId: "approximateOption",
                      searchLabel: "Approximate"
                    },
                    {
                      value: ELocationAccuray.Precise,
                      label: "Precise",
                      testId: "preciseOption",
                      searchLabel: "Precise"
                    }
                  ]}
                />
              </Form.Item>
            </ConfigField>

            <ConfigField label="Background Location" tooltipContentKey="gpstracking_gpstracking_backgroundlocation">
              <Form.Item name="backgroundLocationTrackingEnabled" valuePropName="checked">
                <KuikaSwitch />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
